import { useEffect, useState } from 'react';
import type { AutomationRule, AutomationRun, KillSwitchState } from '@hospasense/contracts';
import { AUTOMATION_ACTIONS, AUTOMATION_TRIGGERS } from '@hospasense/contracts';
import {
  createAutomationRule,
  fetchAutomationRules,
  fetchAutomationRuns,
  fetchDryRun,
  fetchKillSwitch,
  setAutomationRuleEnabled,
  setKillSwitch,
  type DryRunRow,
} from '../lib/queries-s5.js';
import { useAuth } from '../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Select,
  StatusPill,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
  useToast,
} from '../components/ui/index.js';

const RUN_TONE: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  executed: 'success',
  suppressed: 'warning',
  failed: 'error',
  dry_run: 'neutral',
};

export function AutomationPage() {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canManage = hasPermission('automation.manage');

  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [runs, setRuns] = useState<AutomationRun[]>([]);
  const [killSwitch, setKillState] = useState<KillSwitchState | null>(null);
  const [dryRun, setDryRun] = useState<DryRunRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    trigger: 'case.sla_breached',
    action: 'notify_role',
    paramKey: 'role',
    paramValue: 'product_operations',
    maxRunsPerHour: 10,
    switchReason: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const [r, x, k] = await Promise.all([
        fetchAutomationRules(),
        fetchAutomationRuns(),
        fetchKillSwitch(),
      ]);
      setRules(r);
      setRuns(x);
      setKillState(k);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const runDryRun = async () => {
    try {
      setDryRun(await fetchDryRun());
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const toggleRule = async (rule: AutomationRule) => {
    try {
      await setAutomationRuleEnabled(rule.id, !rule.enabled, rule.version);
      toast.success(`Rule ${rule.enabled ? 'disabled' : 'enabled'}`);
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createAutomationRule({
        name: form.name,
        trigger: form.trigger,
        action: form.action,
        actionParams: form.paramKey ? { [form.paramKey]: form.paramValue } : {},
        maxRunsPerHour: form.maxRunsPerHour,
      });
      toast.success('Rule created');
      setCreateOpen(false);
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const submitSwitch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!killSwitch) return;
    setSaving(true);
    try {
      const next = await setKillSwitch(!killSwitch.engaged, form.switchReason);
      setKillState(next);
      toast.success(
        next.engaged ? 'Kill switch ENGAGED — all automations halted' : 'Kill switch disengaged',
      );
      setSwitchOpen(false);
      setForm((f) => ({ ...f, switchReason: '' }));
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="automation-page">
      <PageHeader
        title="Workflow Automation"
        subtitle="Narrow by design: a trigger, one allowlisted low-risk action (notify, task, or a draft approval request), a per-rule rate limit and idempotent, append-only runs. Nothing punitive is expressible."
        actions={
          canManage && (
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              New rule
            </Button>
          )
        }
      />
      {error && <ErrorState description={error} onRetry={() => void load()} />}
      {loading ? (
        <LoadingState label="Loading automation…" />
      ) : (
        <>
          <Card data-testid="kill-switch-card">
            <CardBody>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-text-primary">Global kill switch</h2>
                  <p className="mt-1 text-xs text-text-muted">
                    {killSwitch?.engaged
                      ? `ENGAGED — no automation will execute. ${killSwitch.reason ?? ''}`
                      : 'Disengaged — enabled rules execute within their limits.'}
                    {killSwitch?.changedByDisplayName &&
                      ` (last changed by ${killSwitch.changedByDisplayName})`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill
                    tone={killSwitch?.engaged ? 'error' : 'success'}
                    label={killSwitch?.engaged ? 'Engaged' : 'Disengaged'}
                  />
                  {canManage && (
                    <Button
                      variant={killSwitch?.engaged ? 'secondary' : 'danger'}
                      size="sm"
                      data-testid="kill-switch-toggle"
                      onClick={() => setSwitchOpen(true)}
                    >
                      {killSwitch?.engaged ? 'Disengage…' : 'Engage…'}
                    </Button>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-text-primary">Rules</h2>
              <Button variant="outline" size="sm" onClick={() => void runDryRun()}>
                Dry-run all enabled rules
              </Button>
            </div>
            <TableContainer>
              <Table density="compact">
                <THead>
                  <TR>
                    <TH>Rule</TH>
                    <TH>Trigger</TH>
                    <TH>Action</TH>
                    <TH>Rate limit</TH>
                    <TH>Enabled</TH>
                    {dryRun && <TH>Would act on</TH>}
                    {canManage && <TH align="right">Toggle</TH>}
                  </TR>
                </THead>
                <TBody>
                  {rules.map((r) => {
                    const dr = dryRun?.find((x) => x.ruleId === r.id);
                    return (
                      <TR key={r.id}>
                        <TD className="font-semibold text-text-primary">{r.name}</TD>
                        <TD>
                          <Badge tone="neutral">{r.trigger}</Badge>
                        </TD>
                        <TD>
                          <Badge tone="neutral">{r.action}</Badge>
                        </TD>
                        <TD className="text-xs">{r.maxRunsPerHour}/hour</TD>
                        <TD>
                          <StatusPill
                            tone={r.enabled ? 'success' : 'neutral'}
                            label={r.enabled ? 'enabled' : 'disabled'}
                          />
                        </TD>
                        {dryRun && (
                          <TD className="text-xs tabular-nums">
                            {dr ? `${dr.matches} match(es)` : '—'}
                          </TD>
                        )}
                        {canManage && (
                          <TD align="right">
                            <Button variant="outline" size="sm" onClick={() => void toggleRule(r)}>
                              {r.enabled ? 'Disable' : 'Enable'}
                            </Button>
                          </TD>
                        )}
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </TableContainer>
            {dryRun && (
              <p className="text-xs text-text-muted">
                Dry-run performed no writes and sent nothing — it only reports what each enabled
                rule would act on right now.
              </p>
            )}
          </div>

          <div className="space-y-3">
            <h2 className="text-lg font-bold text-text-primary">Run log (append-only)</h2>
            <TableContainer>
              <Table density="compact">
                <THead>
                  <TR>
                    <TH>When</TH>
                    <TH>Rule</TH>
                    <TH>Status</TH>
                    <TH>Subject</TH>
                    <TH>Detail</TH>
                  </TR>
                </THead>
                <TBody>
                  {runs.map((x) => (
                    <TR key={x.id}>
                      <TD className="text-xs">{new Date(x.ranAt).toLocaleString()}</TD>
                      <TD className="text-xs font-medium">{x.ruleName}</TD>
                      <TD>
                        <StatusPill tone={RUN_TONE[x.status] ?? 'neutral'} label={x.status} />
                      </TD>
                      <TD className="font-mono text-xs">
                        {x.subjectType}:{x.subjectId.slice(0, 8)}
                      </TD>
                      <TD className="text-xs text-text-muted">{x.detail ?? '—'}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableContainer>
            {runs.length === 0 && <p className="text-xs text-text-muted">No runs recorded yet.</p>}
          </div>
        </>
      )}

      <Modal open={createOpen} title="New automation rule" onClose={() => setCreateOpen(false)}>
        <form onSubmit={(e) => void submitCreate(e)} className="space-y-3">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            minLength={5}
            required
          />
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Trigger"
              value={form.trigger}
              onChange={(e) => setForm((f) => ({ ...f, trigger: e.target.value }))}
            >
              {AUTOMATION_TRIGGERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
            <Select
              label="Action (allowlist)"
              value={form.action}
              onChange={(e) => setForm((f) => ({ ...f, action: e.target.value }))}
            >
              {AUTOMATION_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Parameter key"
              value={form.paramKey}
              onChange={(e) => setForm((f) => ({ ...f, paramKey: e.target.value }))}
            />
            <Input
              label="Parameter value"
              value={form.paramValue}
              onChange={(e) => setForm((f) => ({ ...f, paramValue: e.target.value }))}
            />
          </div>
          <Input
            label="Max runs per hour (1–60)"
            type="number"
            min={1}
            max={60}
            value={String(form.maxRunsPerHour)}
            onChange={(e) =>
              setForm((f) => ({ ...f, maxRunsPerHour: parseInt(e.target.value, 10) || 10 }))
            }
            required
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={saving}>
              Create rule
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={switchOpen}
        title={killSwitch?.engaged ? 'Disengage kill switch' : 'Engage kill switch'}
        onClose={() => setSwitchOpen(false)}
      >
        <form onSubmit={(e) => void submitSwitch(e)} className="space-y-3">
          <p className="text-xs text-text-muted">
            {killSwitch?.engaged
              ? 'Automations will resume executing within their limits.'
              : 'ALL automation execution halts immediately, everywhere. Rules stay configured.'}
          </p>
          <Textarea
            label="Reason (min 10 characters, recorded and audited)"
            value={form.switchReason}
            onChange={(e) => setForm((f) => ({ ...f, switchReason: e.target.value }))}
            minLength={10}
            required
            rows={3}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setSwitchOpen(false)}>
              Cancel
            </Button>
            <Button
              variant={killSwitch?.engaged ? 'primary' : 'danger'}
              type="submit"
              loading={saving}
            >
              {killSwitch?.engaged ? 'Disengage' : 'Engage kill switch'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
